import { useState, type FormEvent } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { IconButton } from '@/components/ui/IconButton';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { LogoLoader } from '@/components/ui/logo-loader';

import { CredentialNotificationRetry } from './credential-notification-retry';
import { CredentialOtpInput } from './credential-otp-input';
import { CredentialQrImage } from './credential-qr-image';
import { CredentialRequestOverlay } from './credential-request-overlay';
import {
  type SignInSession,
  useSignInSessionActions,
} from './sign-in-session.query';

export const CredentialRequestForm = ({
  request,
  onBeforeSubmit,
  overlay = false,
}: {
  request: SignInSession;
  onBeforeSubmit?: () => void | Promise<void>;
  overlay?: boolean;
}) => {
  const [values, setValues] = useState<Record<string, string | string[]>>({});
  const [visiblePasswords, setVisiblePasswords] = useState<
    Record<string, boolean>
  >({});
  const [submitted, setSubmitted] = useState(false);
  const { respond, resume } = useSignInSessionActions(
    request.sign_in_session_id,
    request.chat_id,
    request.request?.input_request_id,
    request.expires_at
  );
  const busy = respond.isPending || resume.isPending;
  const step = request.request;
  const pending = request.status === 'active' && step?.status === 'pending';
  const showInput = pending && !busy && (!submitted || respond.isError);
  const cancelled = step?.result?.action === 'close';
  const needsNotification =
    !busy && step?.status === 'resolved' && !step.notified;
  const inProgress =
    busy || (request.status === 'active' && !needsNotification);

  const submit = async (
    event: FormEvent<HTMLFormElement>,
    sectionId: string,
    fields: NonNullable<SignInSession['request']>['sections'][number]['fields']
  ) => {
    event.preventDefault();
    if (!showInput || busy) return;
    if (
      fields.some(
        (field) =>
          field.type === 'otp' &&
          !new RegExp(`^[A-Za-z0-9]{${field.max_length}}$`).test(
            (values[field.id] as string[] | undefined)?.join('') ?? ''
          )
      )
    )
      return;
    const submittedValues = Object.fromEntries(
      fields.map((field) => [
        field.id,
        field.type === 'otp'
          ? (values[field.id] as string[]).join('')
          : ((values[field.id] as string | undefined) ??
            field.initial_value ??
            ''),
      ])
    );
    setValues({});
    setVisiblePasswords({});
    setSubmitted(true);
    await respond.submit(
      { action: 'fill', section_id: sectionId, values: submittedValues },
      onBeforeSubmit
    );
  };

  const sendAction = async (
    action:
      | { action: 'choose'; section_id: string; choice_id: string }
      | { action: 'back' | 'close' }
  ) => {
    if (!showInput || busy) return;
    setValues({});
    setVisiblePasswords({});
    setSubmitted(true);
    await respond.submit(action, onBeforeSubmit);
  };

  if (cancelled || (overlay && respond.dismissed)) return null;
  if (overlay && needsNotification) {
    return (
      <CredentialNotificationRetry
        pending={busy}
        isError={resume.isError}
        onRetry={() => void resume.mutate()}
      />
    );
  }
  if (overlay && !showInput) return null;

  const content = (
    <YStack
      className="min-w-0 gap-5 rounded-xl border border-border bg-surface p-4 sm:p-6"
      data-testid="credential-request-form"
    >
      <XStack className="items-center justify-between gap-2">
        <YStack className="min-w-0 gap-1">
          <h2 className="text-base font-medium">Browser request</h2>
          {showInput ? (
            <p className="break-words text-sm leading-relaxed text-foreground-muted">
              {new URL(step.origin).origin}
            </p>
          ) : null}
        </YStack>
        <XStack className="shrink-0 items-center gap-1">
          {showInput && step.allow_back ? (
            <IconButton
              icon={Icons.arrowLeft}
              strokeWidth={1.8}
              aria-label="Back"
              title="Back"
              size="xl"
              disabled={busy}
              onClick={() => void sendAction({ action: 'back' })}
            />
          ) : null}
          {overlay && showInput ? (
            <IconButton
              icon={Icons.cancel}
              strokeWidth={1.8}
              aria-label="Close sign-in"
              title="Close sign-in"
              size="xl"
              disabled={busy}
              onClick={() => void sendAction({ action: 'close' })}
            />
          ) : null}
        </XStack>
      </XStack>
      {!showInput ? (
        <YStack center className="min-h-36 gap-3 py-6 text-center">
          {inProgress ? (
            <YStack aria-hidden="true" className="size-8 text-foreground">
              <LogoLoader activity="think" flightOnly />
            </YStack>
          ) : null}
          <p role="status" className="text-base font-medium">
            {busy
              ? 'Sending your response…'
              : needsNotification
                ? 'Your response hasn’t reached Littlebird'
                : request.status === 'success'
                  ? 'Signed in'
                  : request.status === 'timedout'
                    ? 'Sign-in timed out'
                    : request.status === 'failure'
                      ? 'Request couldn’t be completed'
                      : 'Working on the next step…'}
          </p>
          {!needsNotification && !busy && request.status !== 'active' ? (
            <p className="text-sm leading-relaxed text-foreground-muted">
              {request.status === 'success'
                ? 'You can return to your conversation.'
                : request.status === 'timedout'
                  ? 'Sign-ins time out after 30 minutes. Ask Littlebird to start a fresh sign-in in your conversation.'
                  : 'Return to your conversation to try again.'}
            </p>
          ) : null}
        </YStack>
      ) : null}
      {showInput && respond.isError ? (
        <p role="alert" className="text-sm text-danger-11">
          Couldn’t send. Please try again.
        </p>
      ) : null}
      {showInput
        ? step.sections.map((section) => (
            <YStack
              key={section.id}
              role="group"
              aria-label={section.label}
              className="gap-3"
            >
              <p className="break-words text-sm leading-relaxed text-foreground-muted">
                {section.label}
              </p>
              {section.fields.length > 0 ? (
                <form
                  onSubmit={(event) =>
                    void submit(event, section.id, section.fields)
                  }
                  autoComplete="off"
                >
                  <YStack className="gap-4">
                    {section.fields.map((field) => (
                      <YStack key={field.id} className="min-w-0 gap-2">
                        <label
                          className="text-sm font-medium"
                          htmlFor={`credential-${request.sign_in_session_id}-${field.id}`}
                        >
                          {field.label}
                        </label>
                        {field.type === 'otp' ? (
                          <CredentialOtpInput
                            id={`credential-${request.sign_in_session_id}-${field.id}`}
                            label={field.label}
                            length={field.max_length}
                            slots={
                              (values[field.id] as string[] | undefined) ?? []
                            }
                            onChange={(slots) =>
                              setValues((current) => ({
                                ...current,
                                [field.id]: slots,
                              }))
                            }
                          />
                        ) : (
                          <XStack className="relative items-center">
                            <input
                              id={`credential-${request.sign_in_session_id}-${field.id}`}
                              name={field.id}
                              type={
                                field.type === 'password' &&
                                visiblePasswords[field.id]
                                  ? 'text'
                                  : field.type === 'redirect_url'
                                    ? 'url'
                                    : field.type
                              }
                              required
                              autoComplete="off"
                              value={
                                (values[field.id] as string | undefined) ??
                                field.initial_value ??
                                ''
                              }
                              onChange={(event) =>
                                setValues((current) => ({
                                  ...current,
                                  [field.id]: event.target.value,
                                }))
                              }
                              className="min-h-11 min-w-0 w-full rounded-lg border border-border bg-background px-3 py-2 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-accent"
                              style={
                                field.type === 'password'
                                  ? { paddingRight: 44 }
                                  : undefined
                              }
                            />
                            {field.type === 'password' ? (
                              <IconButton
                                type="button"
                                icon={
                                  visiblePasswords[field.id]
                                    ? Icons.eyeOff
                                    : Icons.eye
                                }
                                aria-label={
                                  visiblePasswords[field.id]
                                    ? `Hide ${field.label}`
                                    : `Show ${field.label}`
                                }
                                title={
                                  visiblePasswords[field.id]
                                    ? 'Hide password'
                                    : 'Show password'
                                }
                                size="lg"
                                className="absolute right-0.5"
                                onClick={() =>
                                  setVisiblePasswords((current) => ({
                                    ...current,
                                    [field.id]: !current[field.id],
                                  }))
                                }
                              />
                            ) : null}
                          </XStack>
                        )}
                      </YStack>
                    ))}
                    <Button
                      type="submit"
                      className="min-h-11 w-full"
                      disabled={busy}
                    >
                      {section.fields[0]?.type === 'redirect_url'
                        ? 'Open link'
                        : 'Continue'}
                    </Button>
                  </YStack>
                </form>
              ) : null}
              {section.qr_image_file_id ? (
                <CredentialQrImage
                  key={section.qr_image_file_id}
                  fileId={section.qr_image_file_id}
                />
              ) : null}
              {section.choices.map((choice) => (
                <Button
                  key={choice.id}
                  variant="secondary"
                  className="h-auto min-h-11 w-full [&>span]:w-full [&>span]:justify-start [&>span]:break-words [&>span]:text-left [&>span]:whitespace-normal"
                  disabled={busy}
                  onClick={() =>
                    void sendAction({
                      action: 'choose',
                      section_id: section.id,
                      choice_id: choice.id,
                    })
                  }
                >
                  {choice.label}
                </Button>
              ))}
            </YStack>
          ))
        : null}
      {needsNotification && resume.isError ? (
        <p role="alert" className="text-sm text-danger-11">
          Couldn’t send. Please try again.
        </p>
      ) : null}
      {needsNotification ? (
        <Button
          className="min-h-11 w-full"
          disabled={busy}
          onClick={() => void resume.mutate()}
        >
          Send again
        </Button>
      ) : null}
    </YStack>
  );

  return overlay ? (
    <CredentialRequestOverlay label="Browser request">
      {content}
    </CredentialRequestOverlay>
  ) : (
    content
  );
};
